import { type RolodexEntryQueryBody } from 'features/rolodex/hooks/useRolodexEntries'
import { type GeckoEntry, type RolodexEntryType } from 'types'

// MAINTAINER-CONTRACT.IfChange:
// Refresh sync scope starts using new query fields, stops using current fields,
// or changes how entries contribute to scope.
// MAINTAINER-CONTRACT.ThenChange(src/features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/README.md)
// MAINTAINER-CONTRACT.ThenChange(src/features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/internal/refreshSyncScope/getRefreshSyncScope.test.ts)

export function getRefreshSyncScopeFromFetch({
  entries,
  queryOrQueries,
}: RefreshSyncScopeFromFetchInput): ReadonlySet<RolodexEntryType> {
  return getUnionedRefreshSyncScope([
    getRefreshSyncScopeFromQuery(queryOrQueries),
    getRefreshSyncScopeFromEntries(entries),
  ])
}

export function getRefreshSyncScopeFromQuery(
  queryOrQueries:
    | RolodexEntryQueryBody
    | ReadonlyArray<RolodexEntryQueryBody>
    | undefined
): ReadonlySet<RolodexEntryType> {
  return new Set(
    getRolodexQueryBodies(queryOrQueries).flatMap((queryBody) => [
      ...getRolodexTypeSet(queryBody.type),
      ...getRolodexTypeSet(queryBody.links),
    ])
  )
}

export function getRefreshSyncScopeFromEntries(
  entries: ReadonlyArray<GeckoEntry>
): ReadonlySet<RolodexEntryType> {
  return new Set(entries.map((entry) => entry.type))
}

// ----------------------------------------------------------------------------
//                                Helper Fns
// ----------------------------------------------------------------------------

function getRolodexQueryBodies(
  queryOrQueries:
    | RolodexEntryQueryBody
    | ReadonlyArray<RolodexEntryQueryBody>
    | undefined
): ReadonlyArray<RolodexEntryQueryBody> {
  if (!queryOrQueries) {
    return []
  }

  if (isQueryBodyArray(queryOrQueries)) {
    return queryOrQueries
  }

  return [queryOrQueries]
}

function isQueryBodyArray(
  queryOrQueries: RolodexEntryQueryBody | ReadonlyArray<RolodexEntryQueryBody>
): queryOrQueries is ReadonlyArray<RolodexEntryQueryBody> {
  return Array.isArray(queryOrQueries)
}

function getRolodexTypeSet(
  rolodexTypeOrTypes:
    | RolodexEntryType
    | ReadonlyArray<RolodexEntryType>
    | undefined
): ReadonlySet<RolodexEntryType> {
  if (!rolodexTypeOrTypes) {
    return new Set()
  }

  return new Set(
    Array.isArray(rolodexTypeOrTypes)
      ? rolodexTypeOrTypes
      : [rolodexTypeOrTypes]
  )
}

function getUnionedRefreshSyncScope(
  rolodexTypeSets: ReadonlyArray<ReadonlySet<RolodexEntryType>>
): ReadonlySet<RolodexEntryType> {
  return new Set(
    rolodexTypeSets.flatMap((rolodexTypeSet) => Array.from(rolodexTypeSet))
  )
}

// ----------------------------------------------------------------------------
//                                Helper Types
// ----------------------------------------------------------------------------

export interface RefreshSyncScopeFromFetchInput {
  queryOrQueries:
    | RolodexEntryQueryBody
    | ReadonlyArray<RolodexEntryQueryBody>
    | undefined
  entries: ReadonlyArray<GeckoEntry>
}
